/**
 * Cibles — /targets.  Lot 5.
 *
 * Layout matches the onboarding "Qu'est-ce qu'on mesure ?" screens:
 *   - Pill tabs per category (colored active state)
 *   - Description text under the tabs
 *   - Table with identifying columns only
 *   - Inline edit per row ("Modifier" button)
 *   - Search bar above table
 *   - Import CSV + Voir le modèle at bottom
 */
import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputBase,
  LinearProgress,
  Menu,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../services/api';
import { targetsService } from '../services/targets';
import { useBrand } from '../contexts/BrandContext';
import {
  TARGET_LIMITS, type TargetCategory,
} from '@traaker/shared';

// ---------------------------------------------------------------------------
// Sub-module imports
// ---------------------------------------------------------------------------

import {
  type TargetRow, type TagRegistry,
  type CatConfig,
  tagRegistryOf, catCount,
  CATEGORIES, CAT_BY_ID,
} from './targets/CATEGORIES';
import {
  ImportError, downloadCsv,
} from './targets/csv';
import { importHarvestedUrls, importCatalogueCsv, importCategoryCsv } from './targets/importers';
import { useZoneAutocomplete } from './targets/useZoneAutocomplete';
import {
  ChipListEditor, GenericTargetDialog,
  ProductListView, ProductAddForm,
  CollectionListView, ContenuDomainView,
} from './targets/CatalogueViews';
import { EstablishmentAddForm, precisionLevel } from './targets/EstablishmentForm';
import { PersonneAddForm } from './targets/PersonneForm';
import { TargetTableRow } from './targets/TargetTableRow';
import { useTargetsData } from './targets/useTargetsData';
import {
  K, microLabel, Pill, rowActionSx,
  DialogHead, DialogFoot, FieldLabel, dialogPaperProps, dialogContentSx,
  inputSx, selectSx, primaryBtnSx, secondaryBtnSx,
} from './targets/ui';

// ---------------------------------------------------------------------------
// Local style constants
// ---------------------------------------------------------------------------

const alertSx = { borderRadius: '10px', fontSize: 12.5, py: 0.25, alignItems: 'center', '& .MuiAlert-message': { py: 0.75 } } as const;

/** The mock's `.callout` — job banners. */
const calloutSx = {
  display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1,
  borderRadius: '10px', border: '1px solid',
} as const;

/** Bordered toolbar button (tag filter, import menu). */
const toolbarBtnSx = {
  gap: 0.75, fontSize: 12.5, fontWeight: 600, px: 1.25, py: '5px', borderRadius: '10px',
  border: `1px solid ${K.line}`, bgcolor: K.paper, color: K.ink, whiteSpace: 'nowrap',
  '&:hover': { bgcolor: K.primarySoft },
  '&.Mui-focusVisible': { boxShadow: K.focusRing },
} as const;

const menuItemSx = { fontSize: 13, gap: 1.25, py: 0.875, mx: 0.5, borderRadius: '8px' } as const;

// ---------------------------------------------------------------------------
// Category pill tab
// ---------------------------------------------------------------------------

/** The mock's `.tab`: underlined, icon + label + a quiet tabular count. */
function CatTab({ cat, active, count, onClick }: { cat: CatConfig; active: boolean; count: number; onClick: () => void }) {
  const { Icon } = cat;
  const { t } = useTranslation();
  return (
    <ButtonBase
      onClick={onClick}
      role="tab"
      aria-selected={active}
      sx={{
        gap: 0.75,
        px: 1.5,
        pt: 1,
        pb: '9px',
        mb: '-1px',
        borderBottom: '2px solid',
        borderColor: active ? K.primary : 'transparent',
        color: active ? K.primaryDark : K.ink2,
        fontWeight: 600,
        fontSize: 13,
        whiteSpace: 'nowrap',
        transition: 'color .12s, border-color .12s',
        '&:hover': { color: active ? K.primaryDark : K.ink },
        '&.Mui-focusVisible': { boxShadow: K.focusRing, borderRadius: '6px 6px 0 0' },
      }}
    >
      <Icon sx={{ fontSize: 15, color: active ? cat.color : K.ink3 }} />
      <span>{t(cat.labelKey)}</span>
      {count > 0 && (
        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: active ? K.primaryDark : K.ink3, opacity: active ? 0.75 : 1, fontVariantNumeric: 'tabular-nums' }}>
          {count > 99 ? '99+' : count}
        </Box>
      )}
    </ButtonBase>
  );
}

/** Segmented control button (catalogue sub-tabs), same as the Brand Knowledge filter. */
function SegTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        gap: 0.75, fontSize: 12, fontWeight: 600, px: 1.25, py: '5px', borderRadius: '8px', whiteSpace: 'nowrap',
        color: active ? K.ink : K.ink2,
        bgcolor: active ? K.paper : 'transparent',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
        '&:hover': { color: K.ink },
        '&.Mui-focusVisible': { boxShadow: K.focusRing },
      }}
    >
      {children}
    </ButtonBase>
  );
}

// ---------------------------------------------------------------------------
// SitemapImportDialog — sitemap-based bulk import for produit/collection (2026-09-23)
// ---------------------------------------------------------------------------

/**
 * Two-step dialog:
 *   1. Domain → detect sitemaps → pick ONE sitemap → configure type/brand/tags
 *   2. On "Importer": harvest URLs → build NDJSON → staged-import (begin/PUT/commit)
 *
 * Uses the staged NDJSON path so 50k-URL imports don't hit the batch POST 409 loop.
 * Committed with `mode: 'merge'` so the import ADDS to the produit/collection partitions:
 * server-side, existing rows the sitemap doesn't mention are carried over
 * (`_merge_unreplaced_rows`) and the ones it does keep their targetId
 * (`_carry_over_produit_collection`).
 */
function SitemapImportDialog({
  open,
  brandId,
  marques,
  importLocked,
  onClose,
  onSuccess,
}: {
  open: boolean;
  brandId: string;
  marques: TargetRow[];
  importLocked: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  type HarvestResult = {
    urls: Array<{ url: string; name?: string; id: string; type: 'produit' | 'collection' }>;
    truncated?: boolean;
  };

  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [sitemapUrls, setSitemapUrls] = useState<string[] | null>(null);
  const [wafBlocked, setWafBlocked] = useState(false);
  const [selectedSitemap, setSelectedSitemap] = useState('');
  const [harvesting, setHarvesting] = useState(false);
  const [harvestResult, setHarvestResult] = useState<HarvestResult | null>(null);
  const [harvestError, setHarvestError] = useState<string | null>(null);
  const [parentTargetId, setParentTargetId] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const reset = () => {
    setDomain(''); setDetecting(false); setDetectError(null);
    setSitemapUrls(null); setWafBlocked(false); setSelectedSitemap('');
    setHarvesting(false); setHarvestResult(null); setHarvestError(null);
    setParentTargetId(''); setTags([]);
    setImporting(false); setImportError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleDetect = async () => {
    const d = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();
    if (!d) return;
    setDetecting(true); setDetectError(null); setSitemapUrls(null); setWafBlocked(false);
    setSelectedSitemap(''); setHarvestResult(null); setHarvestError(null);
    try {
      const res = await api.post(`/brands/${brandId}/targets/sitemap/discover`, { domain: d }) as {
        sitemapUrls: string[]; wafBlocked: boolean;
      };
      setSitemapUrls(res.sitemapUrls || []);
      setWafBlocked(!!res.wafBlocked);
      if (res.sitemapUrls?.length) setSelectedSitemap(res.sitemapUrls[0]);
    } catch (e: any) {
      setDetectError(e?.message ?? t('cibles.errors.generic'));
    } finally { setDetecting(false); }
  };

  const handleHarvest = async () => {
    if (!selectedSitemap) return;
    setHarvesting(true); setHarvestResult(null); setHarvestError(null); setImportError(null);
    try {
      const res = await api.post(`/brands/${brandId}/targets/sitemap/harvest`, { sitemapUrl: selectedSitemap }) as HarvestResult & { total: number };
      if (!res.urls?.length) { setHarvestError(t('cibles.sitemap.no_sitemaps')); return; }
      setHarvestResult(res);
    } catch (e: any) {
      setHarvestError(e?.message ?? t('cibles.sitemap.error_harvest'));
    } finally { setHarvesting(false); }
  };

  const handleImport = async () => {
    if (!harvestResult?.urls?.length) return;
    setImporting(true); setImportError(null);
    try {
      // Staged merge import of the already-harvested URLs (begin → PUT → commit).
      await importHarvestedUrls(brandId, harvestResult.urls, { parentTargetId, tags });

      if (harvestResult.truncated) alert(t('cibles.sitemap.truncated'));
      onSuccess();
      handleClose();
    } catch (e: any) {
      setImportError(e instanceof ImportError ? t(e.key, e.params) : (e?.message ?? t('cibles.sitemap.error_harvest')));
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
      <DialogHead title={t('cibles.sitemap.dialog_title')} onClose={handleClose} />
      <DialogContent sx={dialogContentSx}>

        {/* Domain input + detect */}
        <Box>
          <FieldLabel>{t('cibles.sitemap.domain_label')}</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('cibles.sitemap.domain_placeholder')}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleDetect(); }}
              disabled={detecting || importing}
              inputProps={{ 'aria-label': t('cibles.sitemap.domain_label') }}
              sx={inputSx}
            />
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={handleDetect}
              disabled={!domain.trim() || detecting || importing}
              sx={[primaryBtnSx, { whiteSpace: 'nowrap', minWidth: 'max-content' }]}
            >
              {detecting ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
              {t(detecting ? 'cibles.sitemap.detecting' : 'cibles.sitemap.detect_button')}
            </Button>
          </Box>
        </Box>

        {detectError && <Alert severity="error" sx={alertSx}>{detectError}</Alert>}

        {sitemapUrls !== null && (
          <>
            {wafBlocked && (
              <Alert severity="warning" sx={alertSx}>
                {t('cibles.sitemap.waf_alert')}
              </Alert>
            )}
            {sitemapUrls.length === 0 ? (
              <Alert severity="info" sx={alertSx}>
                {t('cibles.sitemap.no_sitemaps')}
              </Alert>
            ) : (
              <>
                {/* Sitemap list — radio (one selection) */}
                <Box>
                  <FieldLabel>{t('cibles.sitemap.select_label')}</FieldLabel>
                  <Box sx={{ maxHeight: 168, overflowY: 'auto', border: `1px solid ${K.line}`, borderRadius: '10px' }}>
                    {sitemapUrls.map((u) => (
                      <Box
                        key={u}
                        onClick={() => { setSelectedSitemap(u); setHarvestResult(null); setHarvestError(null); }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.875,
                          cursor: 'pointer', bgcolor: selectedSitemap === u ? K.primarySoft : 'transparent',
                          '&:hover': { bgcolor: selectedSitemap === u ? K.primarySoft : K.rowHover },
                          borderBottom: `1px solid ${K.line}`,
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box sx={{
                          width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                          border: '1.5px solid', borderColor: selectedSitemap === u ? K.primary : '#D4D4D8',
                          boxShadow: selectedSitemap === u ? `inset 0 0 0 3px ${K.paper}` : 'none',
                          bgcolor: selectedSitemap === u ? K.primary : K.paper,
                        }} />
                        <Typography sx={{ fontSize: 12, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', color: K.ink, wordBreak: 'break-all' }}>{u}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Analyse button + harvest results */}
                {!harvestResult && (
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleHarvest}
                      disabled={!selectedSitemap || harvesting || importing}
                      sx={secondaryBtnSx}
                    >
                      {harvesting ? <CircularProgress size={12} color="inherit" sx={{ mr: 1 }} /> : null}
                      {t(harvesting ? 'cibles.sitemap.analysing' : 'cibles.sitemap.analyse_button')}
                    </Button>
                  </Box>
                )}
                {harvestError && <Alert severity="error" sx={alertSx}>{harvestError}</Alert>}
                {harvestResult && (() => {
                  const summary = harvestResult.urls.reduce(
                    (acc, u) => { acc[u.type ?? 'produit']++; return acc; },
                    { produit: 0, collection: 0 },
                  );
                  return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {([
                        ['produit', summary.produit, 'cibles.sitemap.type_product'],
                        ['collection', summary.collection, 'cibles.sitemap.type_collection'],
                      ] as const).map(([k, n, key]) => (
                        <Box key={k} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, px: 1.5, py: 0.875, border: `1px solid ${K.line}`, borderRadius: '10px', bgcolor: k === 'produit' ? K.primarySoft : K.headBg }}>
                          <Typography sx={{ fontFamily: K.tight, fontSize: 16, fontWeight: 700, color: k === 'produit' ? K.primaryDark : K.ink, fontVariantNumeric: 'tabular-nums' }}>{n}</Typography>
                          <Typography sx={{ fontSize: 12.5, color: K.ink2 }}>{t(key)}</Typography>
                        </Box>
                      ))}
                      <Button size="small" variant="text" onClick={handleHarvest} disabled={harvesting || importing}
                        sx={{ fontSize: 12, fontWeight: 600, color: K.ink2, minWidth: 0, px: 1 }}>
                        {t('cibles.sitemap.reanalyse')}
                      </Button>
                    </Box>
                  );
                })()}

                {/* Parent brand (optional) */}
                {marques.length > 0 && (
                  <Box>
                    <FieldLabel>{t('cibles.sitemap.brand_label')}</FieldLabel>
                    <Select
                      size="small"
                      fullWidth
                      value={parentTargetId}
                      onChange={(e) => setParentTargetId(e.target.value)}
                      displayEmpty
                      sx={selectSx}
                    >
                      <MenuItem value="" sx={{ fontSize: 13 }}><em>{t('cibles.sitemap.brand_none')}</em></MenuItem>
                      {marques.map((m) => (
                        <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}

                {/* Tags */}
                <Box>
                  <FieldLabel>{t('cibles.sitemap.tags_label')}</FieldLabel>
                  <ChipListEditor
                    values={tags}
                    onChange={setTags}
                    max={TARGET_LIMITS.maxTagsPerTarget}
                    maxLength={TARGET_LIMITS.maxTagLength}
                    placeholder="tag…"
                    hex={K.primary}
                    disabled={importing}
                  />
                </Box>

                {importError && <Alert severity="error" sx={alertSx}>{importError}</Alert>}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogFoot>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={importing} sx={secondaryBtnSx}>
          {t('cibles.actions.cancel')}
        </Button>
        {sitemapUrls !== null && sitemapUrls.length > 0 && (
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={handleImport}
            disabled={!harvestResult || importing || importLocked || harvesting}
            sx={primaryBtnSx}
          >
            {importing ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
            {t(importing ? 'cibles.sitemap.importing' : 'cibles.sitemap.import_button')}
          </Button>
        )}
      </DialogFoot>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Panel — embedded in the GeoStrategy Cibles card
// ---------------------------------------------------------------------------

/**
 * Core Cibles UI. Embedded only, inside the GeoStrategy Cibles card (D27) —
 * the standalone `/targets` page was removed once the card became the single
 * entry point, along with the `compact` prop that switched between the two.
 */
export function TargetsPanel() {
  const { activeBrand } = useBrand();
  const { t, i18n } = useTranslation(); // i18n.language also picks the CSV template locale

  /**
   * Deep link — `/geo-strategy?cibles=<category>&cibleQ=<text>#cibles`.
   *
   * Read-only: the params are applied as INITIAL state (plus the one-shot search effect
   * below) and never written back, so clicking another tab or clearing the search is not
   * fought by the URL. A param change while the panel is already mounted remounts it —
   * see the `key` on `<TargetsPanel>` in GeoStrategy's `CiblesAccordion`.
   */
  const [searchParams] = useSearchParams();
  const deepLinkQ = searchParams.get('cibleQ')?.trim() ?? '';
  // Validated against the pills the UI actually renders (`CATEGORIES`), which is also what
  // keeps `offre` out: it exists in `TargetCategory` but has no tab, so it falls back to the
  // default like any unknown value — never an empty tab with no pill.
  const deepLinkCat = searchParams.get('cibles');

  const [activeTab, setActiveTab] = useState<TargetCategory>(
    CATEGORIES.some((c) => c.id === deepLinkCat) ? (deepLinkCat as TargetCategory) : 'marque',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Non-failure feedback (e.g. a commit replay) — rendered as an info Alert, not an error. */
  const [notice, setNotice] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [productEditRow, setProductEditRow] = useState<TargetRow | null>(null);
  const [contenuEditRow, setContenuEditRow] = useState<TargetRow | null>(null);
  const [collectionEditRow, setCollectionEditRow] = useState<TargetRow | null>(null);
  const [sitemapDialogOpen, setSitemapDialogOpen] = useState(false);

  // Search
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<TargetRow[]>([]);

  /**
   * Tag filter — IDS, because that is what the rows and the API speak; the chips resolve
   * through `tagRegistry`. Server-side (see `loadPage`), so it resets pagination like a tab
   * switch does rather than filtering the page in place.
   */
  const [tagFilter, setTagFilter] = useState<number[]>([]);
  const [tagMenuAnchor, setTagMenuAnchor] = useState<HTMLElement | null>(null);
  const [importMenuAnchor, setImportMenuAnchor] = useState<HTMLElement | null>(null);

  const brandId = activeBrand?.brandId ?? '';

  // Reset UI state on tab / brand / tag-filter change — mirrors the original
  // `useEffect` that was in TargetsPanel before data loading was extracted.
  useEffect(() => {
    setShowAdd(false); setSearchMode(false); setSearchQ('');
  }, [brandId, activeTab, tagFilter]);

  // Data fetching hook
  const {
    manifest, rows, setRows, marques, loading,
    page, pageCursors, hasNextPage,
    loadSummary, loadPage, loadRows, loadMarques,
  } = useTargetsData({
    brandId,
    activeBrand,
    activeTab,
    tagFilter,
    setError,
  });

  // Import result counters (docs/features/targets.md §20.3, §20.4): when the import lock we were
  // watching clears, read what the worker skipped / merged / could not key. 404 = not
  // written → nothing shown. Only kind `import`: a reshard / delta / delete has no result.
  const [importSummary, setImportSummary] = useState<string[] | null>(null);
  const watchedImportRef = useRef<{ brandId: string; importId: string } | null>(null);
  useEffect(() => {
    const job = manifest?.importJob;
    if (job) {
      if (!job.kind || job.kind === 'import') watchedImportRef.current = { brandId, importId: job.importId };
      return;
    }
    const watched = watchedImportRef.current;
    watchedImportRef.current = null;
    if (!watched || watched.brandId !== brandId) return;
    // A brand switch while the read is in flight must not paint A's summary on B.
    let cancelled = false;
    targetsService
      .getImportResult(watched.brandId, watched.importId)
      .then((res) => {
        if (cancelled) return;
        const c = res?.counters ?? {};
        const lines = (
          [
            ['skippedHeldUrl', c.skippedHeldUrl],
            ['urlOverflow', c.urlOverflow],
            ['stagedDeduped', c.stagedDeduped],
            ['noStableKey', c.noStableKey],
            ['etabNoBranchKey', c.etabNoBranchKey],
            ['etabAmbiguousKey', c.etabAmbiguousKey],
            ['keptDuplicateKey', c.keptDuplicateKey],
          ] as const
        )
          .filter(([, n]) => (n ?? 0) > 0)
          .map(([k, n]) => t(`cibles.importResult.${k}`, { count: n }));
        setImportSummary(lines.length ? lines : null);
      })
      .catch(() => { /* a failed read of an advisory summary is not an import error */ });
    return () => { cancelled = true; };
  }, [manifest?.importJob?.importId, brandId]);
  useEffect(() => { setImportSummary(null); }, [brandId]);

  // Rebuilt whenever the manifest is re-read, which is also how a RENAMED tag reaches the UI:
  // the ids on the rows never change, so nothing else has to be refetched.
  const tagRegistry: TagRegistry = tagRegistryOf(manifest);

  // Zone autocomplete (shared state for one open field at a time)
  const zoneHook = useZoneAutocomplete(1000);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const cat = CAT_BY_ID[activeTab];

  const zoneState = {
    query: zoneHook.query,
    suggestions: zoneHook.suggestions,
    attribution: zoneHook.attribution,
    selected: zoneHook.selected,
    onQueryChange: zoneHook.onQueryChange,
    onSelect: zoneHook.onSelect,
  };

  // ── Actions ──

  // One error surface for every Cibles WRITE. Two statuses need more than a message:
  //
  //   504 — the API stopped WAITING for targets-consolidate; the Lambda itself was NOT
  //         cancelled (aborting a synchronous Invoke never stops the function), so the write
  //         is most likely landing right now. Retrying is the one thing that must not happen:
  //         it queues a SECOND full-partition rewrite. Shown as an info notice, not an error,
  //         and followed by a refresh so the lock/counters the run is producing become
  //         visible — a red "échec" here is what makes a user retry a write that succeeded.
  //   409 TARGETS_CONFLICT — the worker hit the import/reshard lock inside its own CAS loop,
  //         so another write is in flight. Nothing was persisted; transient by construction.
  //         Refresh the summary so the lock that caused it shows up and the controls disable
  //         themselves instead of inviting an immediate retry.
  //
  // Both carry a French `message` from the API (targets.service.ts), so it is displayed
  // verbatim; the local keys are only the fallback for a body that has none.
  const reportWriteError = (e: any, fallbackKey = 'cibles.errors.generic') => {
    if (e instanceof ApiError && e.status === 504) {
      setNotice(e.message || t('cibles.errors.timeout'));
      loadRows(); loadSummary();
      return;
    }
    if (e instanceof ApiError && e.status === 409 && e.body?.code === 'TARGETS_CONFLICT') {
      setError(e.message || t('cibles.errors.conflict'));
      loadSummary();
      return;
    }
    // Reproducible ids (docs/features/targets.md §20.2): a create whose key (url, reference,
    // address…) already belongs to a cible is refused — say which way out, not « conflit ».
    if (e instanceof ApiError && e.status === 409 && e.body?.code === 'TARGETS_KEY_CONFLICT') {
      setError(t('cibles.errors.keyConflict'));
      return;
    }
    if (e instanceof ApiError && e.body?.code === 'TARGETS_CONTENT_LIMIT') {
      // Rendered locally, never the API's sentence: that one is French for every user.
      const limit = Number(e.body?.limit);
      setError(Number.isFinite(limit) && limit > 0
        ? t('cibles.errors.contentLimitN', { limit: limit.toLocaleString(i18n.language) })
        : t('cibles.errors.contentLimit'));
      return;
    }
    if (e instanceof ApiError && e.body?.code === 'TARGETS_ID_IMMUTABLE') {
      setError(t('cibles.errors.idImmutable'));
      return;
    }
    setError(e?.message ?? t(fallbackKey));
  };

  const handleDelete = async (targetId: string, ownerId?: string, category?: string) => {
    try {
      const params = new URLSearchParams();
      if (ownerId) params.set('ownerId', ownerId);
      if (category) params.set('category', category);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await api.delete(`/brands/${brandId}/targets/${targetId}${qs}`) as any;
      if (resp?.status === 'queued') {
        // Large partition: the API queued the delete under the job lock instead of running
        // it in the request. The row is still there until the worker flips; the banner and
        // the poll take over from here.
        setNotice(t('cibles.job.queued_async'));
        loadSummary();
        return;
      }
      setRows((prev) => prev.filter((r) => r.targetId !== targetId));
      loadSummary();
    } catch (e: any) { reportWriteError(e); }
  };

  const handleAdd = async (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[]; relatedTargetIds?: string[] }) => {
    setSaving(true);
    try {
      const resp = await api.post(`/brands/${brandId}/targets`, { targets: [{ ownerId: 'own', category: activeTab, ...data }] }) as any;
      if (resp?.status === 'queued') setNotice(t('cibles.job.queued_async'));
      setShowAdd(false); zoneHook.reset();
      loadRows(); loadSummary();
      if (activeTab === 'marque') loadMarques();
    } catch (e: any) { reportWriteError(e); } finally { setSaving(false); }
  };

  const handleSave = async (targetId: string, data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[]; relatedTargetIds?: string[] }) => {
    // Send the existing targetId so Python removes old stale signal rows for this
    // target before writing the new ones (prevents orphan signal accumulation).
    // Do NOT delete+recreate — that assigns a new UUID and leaves old signals forever.
    //
    // Report AND re-throw: the dialog's onSave re-throws on failure so it stays open,
    // giving the user the error context rather than silently closing.
    try {
      const resp = await api.post(`/brands/${brandId}/targets`, {
        targets: [{ targetId, ownerId: 'own', category: activeTab, ...data }],
      }) as any;
      if (resp?.status === 'queued') setNotice(t('cibles.job.queued_async'));
      loadRows(); loadSummary();
      if (activeTab === 'marque') loadMarques();
      return resp;
    } catch (e: any) {
      reportWriteError(e);
      throw e;
    }
  };

  // Search — explicit (Enter / the Chercher button), never per keystroke, so the
  // `targets_search` budget (600/h) is not a concern for a normal user. A 429 is still
  // possible on repeated submits: keep whatever is already on screen rather than
  // clearing the list or raising an error for something the user will simply retry.
  //
  // `q` defaults to the box's content; it is passed explicitly only by the deep-link effect
  // below, which cannot read the state it just set.
  const handleSearch = async (q: string = searchQ) => {
    if (!q.trim()) return;
    setSearching(true); setSearchMode(true);
    try {
      const data = await api.get(`/brands/${brandId}/targets/search?q=${encodeURIComponent(q)}&category=${activeTab}`) as any;
      setSearchResults(data.results ?? []);
    } catch (e: any) {
      if (!(e instanceof ApiError && e.status === 429)) setError(e?.message ?? t('cibles.errors.generic'));
    } finally { setSearching(false); }
  };

  const clearSearch = () => { setSearchMode(false); setSearchQ(''); setSearchResults([]); };

  // `?cibleQ=` — prefill the box and submit it once, exactly as the user would.
  //
  // Waits for `brandId` (BrandContext resolves after the first render, and the search URL
  // needs it) and is declared AFTER the reset effect above, so the `setSearchQ('')` of that
  // effect's own run in the same commit does not swallow the prefill. The ref keeps it to one
  // fetch (StrictMode double-invokes effects) and is what lets the user clear the search
  // afterwards without it coming back.
  const deepLinkSearchDone = useRef(false);
  useEffect(() => {
    if (!deepLinkQ || !brandId || deepLinkSearchDone.current) return;
    deepLinkSearchDone.current = true;
    setSearchQ(deepLinkQ);
    handleSearch(deepLinkQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, deepLinkQ]);

  // Contenus bulk save.
  //
  // Import
  const handleImport = async (file: File) => {
    setSaving(true);
    try {
      // ── Produit/collection: merge-aware direct POST (avoids duplicates on re-import) ──
      if (activeTab === 'produit' || activeTab === 'collection') {
        const { warnings } = await importCatalogueCsv(brandId, file, marques, {
          onQueued: () => setNotice(t('cibles.job.queued_async')),
        });
        if (warnings.length) setNotice(warnings.slice(0, 5).join('\n') + (warnings.length > 5 ? `\n… et ${warnings.length - 5} autre(s)` : ''));
        loadRows(); loadSummary();
        return;
      }

      // ── Other categories: standard NDJSON bulk import (staged, no merge) ──
      const { warnings: importWarnings, alreadyCommitted } = await importCategoryCsv(brandId, file, activeTab, marques);
      if (importWarnings.length) {
        setNotice(importWarnings.slice(0, 5).join('\n') + (importWarnings.length > 5 ? `\n… et ${importWarnings.length - 5} autre(s)` : ''));
      }
      if (alreadyCommitted) setNotice(t('cibles.import.already_committed'));
      loadSummary();
    } catch (e: any) {
      if (e instanceof ImportError) setError(t(e.key, e.params));
      else reportWriteError(e, 'cibles.errors.import');
    } finally { setSaving(false); }
  };

  // Locks
  const importLocked = !!manifest?.importJob;
  const geocodeLocked = !!manifest?.geocodeJob;
  const tabLocked = activeTab === 'etablissement' ? (importLocked || geocodeLocked) : importLocked;

  const displayRows = searchMode ? searchResults : rows;
  const totalCount = CATEGORIES.reduce((s, c) => s + catCount(manifest, c.id), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>

      {/* ── Card header ── the counts read off `manifest`, not the brand row's
          cached `targetsMeta`, so they stay correct after an in-place edit. */}
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5, flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontFamily: K.tight, fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: K.ink }}>{t('cibles.title')}</Typography>
            <Tooltip title={<Box sx={{ p: 0.5, maxWidth: 270 }}><Typography variant="caption" fontWeight={700} display="block" gutterBottom>{t('cibles.determinism_title')}</Typography><Typography variant="caption">{t('cibles.determinism')}</Typography></Box>} placement="bottom-start" arrow>
              <IconButton size="small" aria-label={t('cibles.determinism_title')} sx={{ p: '3px', color: K.ink3, '&:hover': { color: K.primary, bgcolor: 'transparent' } }}>
                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {/* What it is FOR — one sentence, same style as the Brand Knowledge sections */}
          <Typography sx={{ mt: 0.375, fontSize: 12, fontWeight: 500, color: K.ink, lineHeight: 1.5, maxWidth: 760 }}>
            {t('cibles.purpose')}
          </Typography>
          {/* Description — always visible; helps new users understand the purpose at a glance */}
          <Typography sx={{ mt: 0.375, fontSize: 12.5, color: K.ink2, lineHeight: 1.55, maxWidth: 760 }}>
            {t('cibles.explainer')}
          </Typography>
        </Box>

        {/* ── Signal gauge ── */}
        {(() => {
          const signalCount = manifest?.signalCount ?? 0;
          const maxSignals = 500_000;
          const pct = Math.min(100, (signalCount / maxSignals) * 100);
          const color = pct >= 95 ? '#EF4444' : pct >= 80 ? '#F59E0B' : '#10B981';
          return (
            <Box sx={{ flex: '0 0 200px', border: `1px solid ${K.line}`, borderRadius: '10px', px: 1.5, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Box sx={{ ...microLabel, flex: 1 }}>{t('cibles.signals_cap')}</Box>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5, maxWidth: 280 }}>
                      <Typography variant="caption" fontWeight={700} display="block" gutterBottom>{t('cibles.glossary.signals_title')}</Typography>
                      <Typography variant="caption" display="block">{t('cibles.glossary.signals_body')}</Typography>
                    </Box>
                  }
                  placement="bottom-end"
                  arrow
                >
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: pct >= 80 ? color : K.ink2, cursor: 'help', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {signalCount.toLocaleString(i18n.language)} / {maxSignals.toLocaleString(i18n.language)}
                  </Typography>
                </Tooltip>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  mt: 0.75,
                  height: 5,
                  borderRadius: 999,
                  bgcolor: '#F1F1F3',
                  '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 999 },
                }}
              />
            </Box>
          );
        })()}
      </Box>

      {/* ── Job banners ── */}
      {(importLocked || geocodeLocked) && (
        <Box sx={{ px: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75, mb: 0.5 }}>
          {importLocked && (
            <Box sx={{ ...calloutSx, bgcolor: 'rgba(14,165,233,.07)', borderColor: 'rgba(14,165,233,.2)' }}>
              <HourglassBottomIcon sx={{ fontSize: 15, color: '#0284C7' }} />
              <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#0369A1' }}>{manifest?.importJob?.kind === 'delta' || manifest?.importJob?.kind === 'delete'
                ? t('cibles.job.editing')
                : manifest?.importJob?.status === 'queued' ? t('cibles.import.queued') : t('cibles.job.processing')}</Typography><LinearProgress sx={{ mt: 0.75, height: 3, borderRadius: 999, bgcolor: 'rgba(14,165,233,.15)', '& .MuiLinearProgress-bar': { bgcolor: '#0284C7' } }} /></Box>
            </Box>
          )}
          {geocodeLocked && (
            <Box sx={{ ...calloutSx, bgcolor: 'rgba(245,158,11,.08)', borderColor: 'rgba(245,158,11,.22)' }}>
              <HourglassBottomIcon sx={{ fontSize: 15, color: '#D97706' }} />
              <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 12.5, fontWeight: 600, color: K.warningInk }}>{t('cibles.job.geocoding', { resolved: manifest?.geocodeJob?.resolved ?? 0, rows: manifest?.geocodeJob?.rows ?? 0 })}</Typography><LinearProgress variant="determinate" value={Math.round(((manifest?.geocodeJob?.resolved ?? 0) / (manifest?.geocodeJob?.rows || 1)) * 100)} sx={{ mt: 0.75, height: 3, borderRadius: 999, bgcolor: 'rgba(245,158,11,.15)', '& .MuiLinearProgress-bar': { bgcolor: '#D97706' } }} /></Box>
            </Box>
          )}
        </Box>
      )}

      {(error || notice || importSummary) && (
        <Box sx={{ px: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75, mb: 0.5 }}>
          {error && <Alert severity="error" onClose={() => setError(null)} sx={alertSx}>{error}</Alert>}
          {notice && <Alert severity="info" onClose={() => setNotice(null)} sx={[alertSx, { whiteSpace: 'pre-line' }]}>{notice}</Alert>}
          {importSummary && (
            <Alert severity="info" onClose={() => setImportSummary(null)} sx={alertSx}>
              <Typography sx={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>{t('cibles.importResult.title')}</Typography>
              {importSummary.map((line) => <Box key={line}>{line}</Box>)}
            </Alert>
          )}
        </Box>
      )}

      {/* ── Category tabs ── */}
      <Box sx={{ px: 2.5, pt: 1, flexShrink: 0 }}>
        <Box sx={{ ...microLabel, mb: 0.25 }}>
          {t('cibles.section.measured')}
        </Box>
        <Box role="tablist" sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', borderBottom: `1px solid ${K.line}` }}>
          {CATEGORIES.filter((c) => c.id !== 'collection').map((c) => {
            if (c.id === 'produit') {
              // "Catalogue" tab represents both produit + collection
              const catalogueCount = catCount(manifest, 'produit') + catCount(manifest, 'collection');
              const catalogueActive = activeTab === 'produit' || activeTab === 'collection';
              const catalogueCat = { ...c, labelKey: 'cibles.tab.catalogue' };
              return (
                <CatTab
                  key="catalogue"
                  cat={catalogueCat}
                  active={catalogueActive}
                  count={catalogueCount}
                  onClick={() => setActiveTab(activeTab === 'collection' ? 'collection' : 'produit')}
                />
              );
            }
            return (
              <CatTab key={c.id} cat={c} active={activeTab === c.id} count={catCount(manifest, c.id)} onClick={() => setActiveTab(c.id)} />
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', pt: 1.5 }}>
          {/* ── Sub-tabs for Catalogue (visible only when catalogue is active) ── */}
          {(activeTab === 'produit' || activeTab === 'collection') && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: K.segBg, borderRadius: '10px', p: '2px', flexShrink: 0 }}>
              {(['produit', 'collection'] as const).map((sub) => {
                const subCat = CAT_BY_ID[sub];
                const n = catCount(manifest, sub);
                return (
                  <SegTab key={sub} active={activeTab === sub} onClick={() => setActiveTab(sub)}>
                    <subCat.Icon sx={{ fontSize: 14, color: activeTab === sub ? subCat.color : K.ink3 }} />
                    {t(subCat.labelKey)}
                    {n > 0 && (
                      <Box component="span" sx={{ color: K.ink3, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                        {n > 999 ? `${Math.round(n / 1000)}k` : n}
                      </Box>
                    )}
                  </SegTab>
                );
              })}
            </Box>
          )}
          <Typography sx={{ flex: 1, minWidth: 220, fontSize: 12.5, color: K.ink2, lineHeight: 1.5 }}>
            {t(cat.descKey)}
          </Typography>
        </Box>
      </Box>

      {/* ── Toolbar: search · tag filter · import · add ── */}
      <Box sx={{ px: 2.5, pt: 1.5, pb: 1.25, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', flex: '1 1 260px', maxWidth: 420, minWidth: 200,
              border: `1px solid ${K.line}`, borderRadius: '10px', bgcolor: K.paper, pl: 1, pr: '3px',
              '&:focus-within': { borderColor: K.primary, boxShadow: K.focusRing },
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: K.ink3, flexShrink: 0 }} />
            <InputBase
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('cibles.search.placeholder', { cat: t(cat.labelKey).toLowerCase() })}
              inputProps={{ 'aria-label': t('cibles.search.placeholder', { cat: t(cat.labelKey).toLowerCase() }) }}
              sx={{ flex: 1, fontSize: 12.5, px: 0.75, py: '5px' }}
            />
            {searchMode ? (
              <IconButton size="small" aria-label={t('cibles.search.see_all')} onClick={clearSearch} sx={{ p: '4px', color: K.ink3 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : (
              <ButtonBase
                onClick={() => handleSearch()}
                disabled={searching || !searchQ.trim()}
                sx={{
                  fontSize: 12, fontWeight: 600, px: 1.1, py: '4px', borderRadius: '7px', color: K.primaryDark,
                  '&:hover': { bgcolor: K.primarySoft },
                  '&.Mui-disabled': { color: K.ink3 },
                }}
              >
                {searching ? <CircularProgress size={12} sx={{ color: K.primary }} /> : t('cibles.search.button')}
              </ButtonBase>
            )}
          </Box>

          {/* Filtrer par tag.
              Hidden entirely when the brand has defined no tag: an empty menu is worse than no
              button, and the registry is brand-wide so this is the same test on every tab. */}
          {tagRegistry.size > 0 && (
            <ButtonBase
              onClick={(e: React.MouseEvent<HTMLElement>) => setTagMenuAnchor(e.currentTarget)}
              aria-haspopup="true"
              sx={{
                ...toolbarBtnSx,
                ...(tagFilter.length ? { borderColor: 'transparent', bgcolor: K.primarySoft2, color: K.primaryDark } : {}),
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 14 }} />
              {t('cibles.tags.filter')}
              {tagFilter.length > 0 && (
                <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>· {tagFilter.length}</Box>
              )}
              <KeyboardArrowDownIcon sx={{ fontSize: 15, ml: -0.25, color: tagFilter.length ? K.primaryDark : K.ink3 }} />
            </ButtonBase>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Import menu — template, file, sitemap. Import is gated on importLocked only:
              commitImport is exempt from the geocode lock server-side, so greying it out
              during a geocode sweep would be stricter than the API. */}
          <input ref={fileRef} type="file" accept=".csv,.ndjson,.ndjson.gz" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f && !importLocked) handleImport(f); }} />
          <ButtonBase
            onClick={(e: React.MouseEvent<HTMLElement>) => setImportMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            sx={toolbarBtnSx}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 15 }} />
            {t('cibles.toolbar.import')}
            <KeyboardArrowDownIcon sx={{ fontSize: 15, ml: -0.25, color: K.ink3 }} />
          </ButtonBase>
          <Menu
            anchorEl={importMenuAnchor}
            open={!!importMenuAnchor}
            onClose={() => setImportMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 240, borderRadius: '12px', border: `1px solid ${K.line}`, boxShadow: '0 12px 28px rgba(15,15,30,.12)' } } }}
          >
            <Box sx={{ ...microLabel, px: 2, pt: 0.75, pb: 0.75 }}>
              {t('cibles.footer.import_label', { cat: t(cat.labelKey).toLowerCase() })}
            </Box>
            <MenuItem
              disabled={importLocked || saving}
              onClick={() => { setImportMenuAnchor(null); fileRef.current?.click(); }}
              sx={menuItemSx}
            >
              <FileUploadOutlinedIcon sx={{ fontSize: 16, color: K.ink2 }} />
              {t('cibles.footer.import')}
            </MenuItem>
            {/* Sitemap import — produit/collection only */}
            {(activeTab === 'produit' || activeTab === 'collection') && (
              <MenuItem
                disabled={importLocked || saving}
                onClick={() => { setImportMenuAnchor(null); setSitemapDialogOpen(true); }}
                sx={menuItemSx}
              >
                <AccountTreeOutlinedIcon sx={{ fontSize: 16, color: K.ink2 }} />
                {t('cibles.footer.import_sitemap')}
              </MenuItem>
            )}
            <Divider sx={{ my: '4px !important' }} />
            <MenuItem
              onClick={() => {
                setImportMenuAnchor(null);
                const lang = (i18n.language ?? 'fr').slice(0, 2);
                const template = cat.csvTemplates[lang] ?? cat.csvTemplates['fr'];
                downloadCsv(`template-${cat.id}.csv`, template);
              }}
              sx={menuItemSx}
            >
              <DownloadIcon sx={{ fontSize: 16, color: K.ink2 }} />
              {t('cibles.footer.template')}
            </MenuItem>
          </Menu>

          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => setShowAdd(true)}
            disabled={tabLocked}
            sx={{ fontSize: 12.5, fontWeight: 600, borderRadius: '10px', px: 1.5, py: '5px', bgcolor: K.primary, boxShadow: 'none', '&:hover': { bgcolor: K.primaryDark, boxShadow: 'none' } }}
          >
            {t(cat.addLabelKey)}
          </Button>
        </Box>

        {/* Active tags are echoed OUTSIDE the menu: with the menu shut, a filtered list is
            otherwise indistinguishable from a brand that has few cibles. */}
        {(searchMode || tagFilter.length > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap', fontSize: 12, color: K.ink2 }}>
            {searchMode && (
              <>
                <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{t('cibles.search.results', { count: searchResults.length })}</Box>
                <ButtonBase onClick={clearSearch} sx={{ fontSize: 12, fontWeight: 600, color: K.primaryDark, '&:hover': { textDecoration: 'underline' } }}>
                  {t('cibles.search.see_all')}
                </ButtonBase>
              </>
            )}
            {searchMode && tagFilter.length > 0 && <Box component="span" sx={{ color: K.ink3 }}>·</Box>}
            {tagFilter.map((id) => {
              const name = tagRegistry.get(id);
              if (!name) return null;
              return (
                <Pill key={id} tone="accent" hex={K.primaryDark} onClick={() => setTagFilter((prev) => prev.filter((x) => x !== id))}>
                  {name}
                  <CloseIcon sx={{ fontSize: 12, ml: 0.25 }} />
                </Pill>
              );
            })}
            {tagFilter.length > 0 && (
              <ButtonBase onClick={() => setTagFilter([])} sx={{ fontSize: 12, fontWeight: 600, color: K.ink2, '&:hover': { color: K.ink, textDecoration: 'underline' } }}>
                {t('cibles.tags.filter_clear')}
              </ButtonBase>
            )}
          </Box>
        )}

        <Popover
          open={!!tagMenuAnchor}
          anchorEl={tagMenuAnchor}
          onClose={() => setTagMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 0.5, boxShadow: '0 12px 28px rgba(15,15,30,.12)', border: `1px solid ${K.line}`, borderRadius: '12px', minWidth: 220, maxHeight: 340 } }}
        >
          <Box sx={{ py: 0.5 }}>
            {[...tagRegistry.entries()].map(([id, name]) => {
              const on = tagFilter.includes(id);
              return (
                <MenuItem
                  key={id}
                  // Multi-select, and the menu stays open: filtering by two tags is one
                  // thought, not two trips through the menu.
                  onClick={() => setTagFilter((prev) => (on ? prev.filter((x) => x !== id) : [...prev, id]))}
                  sx={{ ...menuItemSx, fontWeight: on ? 600 : 400, color: on ? K.primaryDark : K.ink }}
                >
                  <Box sx={{
                    width: 14, height: 14, borderRadius: '4px', flexShrink: 0,
                    border: `1.5px solid ${on ? K.primary : '#D4D4D8'}`,
                    bgcolor: on ? K.primary : K.paper,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                  }}>
                    {on && <CheckIcon sx={{ fontSize: 11 }} />}
                  </Box>
                  {name}
                </MenuItem>
              );
            })}
          </Box>
        </Popover>
      </Box>

      {/* ── Table ── */}
      <Box sx={{ mx: 2.5, mb: 1.5, border: `1px solid ${K.line}`, borderRadius: '12px', bgcolor: K.paper, overflow: 'hidden' }}>

            {/* Amber precision banner — établissement only */}
            {activeTab === 'etablissement' && (() => {
              const imprecise = rows.filter((r) => precisionLevel(r) < 3);
              if (imprecise.length === 0) return null;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.125, bgcolor: 'rgba(245,158,11,.07)', borderBottom: `1px solid ${K.line}` }}>
                  <Box sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '8px', bgcolor: 'rgba(245,158,11,.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: K.warningInk }}>
                    <WarningAmberIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: K.warningInk, fontSize: 12.5 }}>
                      {t('cibles.precision.banner', { count: imprecise.length })}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#7C5A1E', lineHeight: 1.45 }}>
                      {t('cibles.precision.banner_detail')}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* Column headers — not shown for produit/contenu/collection (custom layouts) */}
            {displayRows.length > 0 && activeTab !== 'contenu' && activeTab !== 'produit' && activeTab !== 'collection' && (
              <Box sx={{ display: 'flex', alignItems: 'center', px: 0, minHeight: 34, bgcolor: K.headBg, borderBottom: `1px solid ${K.line}` }}>
                {cat.hasParent && (
                  <Box sx={{ ...microLabel, width: 140, flexShrink: 0, px: 1.5 }}>
                    {t('cibles.parent_column')}
                  </Box>
                )}
                {cat.id === 'etablissement' ? (
                  <>
                    <Box sx={{ ...microLabel, flex: 1, minWidth: 0, px: 3 }}>
                      {t('cibles.tab.etablissement')}
                    </Box>
                    <Box sx={{ ...microLabel, width: 300, flexShrink: 0, pr: 3 }}>
                      {t('cibles.precision.col_header')}
                    </Box>
                    <Box sx={{ ...microLabel, width: 172, flexShrink: 0 }}>
                      {t('cibles.precision.col_contact')}
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Colour-swatch column — marque only, keeps the heads above their values */}
                    {cat.id === 'marque' && <Box sx={{ width: 32, flexShrink: 0 }} />}
                    {cat.cols.map((col) => (
                      <Box key={col.field} sx={{ ...microLabel, width: col.width, flexShrink: 0, px: 1.5 }}>
                        {t(col.labelKey)}
                        {col.optional && <Box component="span" sx={{ ml: 0.5, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>{t('cibles.col_optional')}</Box>}
                        {col.field === 'placeId' && (
                          <Tooltip
                            title={
                              <Box sx={{ p: 0.5, maxWidth: 270 }}>
                                <Typography variant="caption" fontWeight={700} display="block" gutterBottom>{t('cibles.placeid.title')}</Typography>
                                <Typography variant="caption" component="div" sx={{ lineHeight: 1.6 }}>
                                  <Trans
                                    i18nKey="cibles.placeid.body"
                                    components={{
                                      mono: <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.15)', px: 0.4, borderRadius: 0.5 }} />,
                                      mono2: <Box component="span" sx={{ fontFamily: 'monospace' }} />,
                                      mono3: <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} />,
                                    }}
                                  />
                                </Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 11, ml: 0.4, verticalAlign: 'middle', opacity: 0.6, cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            )}

            {/* Rows */}
            {loading && displayRows.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={22} sx={{ color: K.primary }} />
              </Box>
            ) : displayRows.length === 0 && !showAdd ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 6, px: 3, gap: 0.75 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: cat.tint, color: cat.color, mb: 0.5 }}>
                  <cat.Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: K.ink }}>{t('cibles.empty_cat', { cat: t(cat.labelKey).toLowerCase() })}</Typography>
                {!searchMode && (
                  <Typography sx={{ fontSize: 12.5, color: K.ink2, maxWidth: 420, lineHeight: 1.5 }}>{t('cibles.empty_hint')}</Typography>
                )}
                <Button variant="outlined" size="small" startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />} onClick={() => setShowAdd(true)}
                  sx={{ mt: 1, fontSize: 12.5, fontWeight: 600, borderRadius: '10px', borderColor: K.line, color: K.ink, '&:hover': { borderColor: K.primary, bgcolor: K.primarySoft } }}>
                  {t(cat.addLabelKey)}
                </Button>
              </Box>
            ) : activeTab === 'collection' ? (
              <CollectionListView
                rows={displayRows}
                locked={tabLocked}
                marques={marques}
                tagRegistry={tagRegistry}
                onDelete={(targetId) => handleDelete(targetId, 'own', 'collection')}
                onEdit={(row) => setCollectionEditRow(row)}
              />
            ) : activeTab === 'produit' ? (
              <ProductListView
                rows={displayRows}
                locked={tabLocked}
                marques={marques}
                onDelete={(targetId) => handleDelete(targetId, 'own', 'produit')}
                onEdit={(row) => setProductEditRow(row)}
              />
            ) : activeTab === 'contenu' ? (
              <ContenuDomainView
                rows={displayRows}
                locked={tabLocked}
                marques={marques}
                onDelete={(targetId) => handleDelete(targetId, 'own', 'contenu')}
                onEdit={(row) => setContenuEditRow(row)}
              />
            ) : (
              <>
                {displayRows.map((row) => {
                  const isRootMarque =
                    activeTab === 'marque' &&
                    row.fields.some(
                      (f) =>
                        f.name === 'name' &&
                        String(f.value).toLowerCase().trim() ===
                          activeBrand?.name?.toLowerCase().trim(),
                    );
                  return (
                  <TargetTableRow
                    key={row.targetId}
                    row={row}
                    cat={cat}
                    marques={marques}
                    locked={tabLocked}
                    isRoot={isRootMarque}
                    onDelete={isRootMarque ? () => {} : () => handleDelete(row.targetId, row.ownerId, row.category)}
                    onSave={(data) => handleSave(row.targetId, data)}
                    onColorChange={activeTab === 'marque' ? async (color) => {
                      try {
                        await api.put(`/brands/${brandId}/targets/${row.targetId}`, {
                          color,
                          ownerId: row.ownerId,
                          category: row.category,
                        });
                      } catch (e) {
                        reportWriteError(e, 'cibles.errors.color');
                        throw e;
                      }
                    } : undefined}
                    tagRegistry={tagRegistry}
                    workingLang={activeBrand?.strategy?.workingLanguage ?? 'fr'}
                  />
                  );
                })}
              </>
            )}

            {/* Paginator */}
            {!searchMode && displayRows.length > 0 && (
              <Box sx={{ px: 1.5, py: 0.75, borderTop: `1px solid ${K.line}`, bgcolor: K.headBg, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={page === 0 || loading}
                  onClick={() => loadPage(page - 1, pageCursors)}
                  sx={rowActionSx()}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: K.ink2, minWidth: 60, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {loading ? <CircularProgress size={12} sx={{ color: K.primary }} /> : (() => {
                    const total = catCount(manifest, activeTab);
                    const pageSize = activeTab === 'produit' ? 10 : activeTab === 'contenu' ? 200 : 50;
                    const totalPages = total > 0 ? Math.ceil(total / pageSize) : null;
                    return totalPages ? `${page + 1} / ${totalPages}` : t('cibles.page_short', { page: page + 1 });
                  })()}
                </Typography>
                <IconButton
                  size="small"
                  disabled={!hasNextPage || loading}
                  onClick={() => loadPage(page + 1, pageCursors)}
                  sx={rowActionSx()}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
      </Box>

      {/* Add dialogs — all categories use dialogs, never inline */}
      <GenericTargetDialog
        open={showAdd && cat.id !== 'etablissement' && cat.id !== 'personne' && cat.id !== 'produit'}
        cat={cat}
        marques={marques}
        brandId={brandId}
        onSave={async (data) => { await handleAdd(data); setShowAdd(false); }}
        onClose={() => setShowAdd(false)}
        saving={saving}
        tagRegistry={tagRegistry}
      />
      <EstablishmentAddForm open={showAdd && activeTab === 'etablissement'} marques={marques} onAdd={handleAdd} onClose={() => { setShowAdd(false); zoneHook.reset(); }} saving={saving} zoneState={zoneState} tagRegistry={tagRegistry} />
      <PersonneAddForm open={showAdd && activeTab === 'personne'} marques={marques} onAdd={handleAdd} onClose={() => setShowAdd(false)} saving={saving} tagRegistry={tagRegistry} workingLang={activeBrand?.strategy?.workingLanguage ?? 'fr'} />
      <ProductAddForm
        open={showAdd && activeTab === 'produit'}
        marques={marques}
        onSave={async (data) => { await handleAdd(data); setShowAdd(false); }}
        onClose={() => setShowAdd(false)}
        saving={saving}
        tagRegistry={tagRegistry}
      />
      {/* Product edit dialog */}
      {productEditRow && (
        <ProductAddForm
          open={!!productEditRow}
          mode="edit"
          initialRow={productEditRow}
          marques={marques}
          onSave={async (data) => { await handleSave(productEditRow.targetId, data); setProductEditRow(null); }}
          onClose={() => setProductEditRow(null)}
          saving={saving}
          tagRegistry={tagRegistry}
        />
      )}
      {/* Collection edit dialog */}
      {collectionEditRow && (
        <GenericTargetDialog
          open={!!collectionEditRow}
          mode="edit"
          cat={CAT_BY_ID['collection']}
          initialRow={collectionEditRow}
          marques={marques}
          onSave={async (data) => { await handleSave(collectionEditRow.targetId, data); setCollectionEditRow(null); }}
          onClose={() => setCollectionEditRow(null)}
          saving={saving}
          tagRegistry={tagRegistry}
        />
      )}
      {/* Contenu edit dialog */}
      {contenuEditRow && (
        <GenericTargetDialog
          open={!!contenuEditRow}
          mode="edit"
          cat={CAT_BY_ID['contenu']}
          initialRow={contenuEditRow}
          marques={marques}
          brandId={brandId}
          onSave={async (data) => { await handleSave(contenuEditRow.targetId, data); setContenuEditRow(null); }}
          onClose={() => setContenuEditRow(null)}
          saving={saving}
          tagRegistry={tagRegistry}
        />
      )}

      {/* ── Footer ── Tout supprimer, for any category with rows */}
      {totalCount > 0 && catCount(manifest, activeTab) > 0 && (
        <Box sx={{ px: 2.5, pb: 1.5, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: '15px !important' }} />}
            disabled={tabLocked || saving}
            onClick={async () => {
              if (!window.confirm(t('cibles.footer.delete_all_confirm', { cat: t(cat.labelKey), interpolation: { escapeValue: false } }))) return;
              setSaving(true);
              try {
                await api.delete(`/brands/${brandId}/targets?category=${activeTab}&ownerId=own`);
                setRows([]); loadRows(); loadSummary();
              } catch (e: any) { reportWriteError(e); }
              finally { setSaving(false); }
            }}
            sx={{ fontSize: 12, fontWeight: 600, borderRadius: '8px', color: K.ink3, '&:hover': { color: K.errorInk, bgcolor: 'rgba(239,68,68,.07)' } }}
          >
            {t('cibles.footer.delete_all')}
          </Button>
        </Box>
      )}

      {/* Sitemap import dialog — produit/collection only */}
      <SitemapImportDialog
        open={sitemapDialogOpen}
        brandId={brandId}
        marques={marques}
        importLocked={importLocked}
        onClose={() => setSitemapDialogOpen(false)}
        onSuccess={() => { loadRows(); loadSummary(); }}
      />
    </Box>
  );
}
